import React, { useState } from "react";

/* Listas da resposta do assistente, desenhadas em cartões. Vêm prontas do
   servidor (mesmo escopo e máscara das ferramentas): o texto do modelo com uma
   consulta por linha, cheio de travessões e negrito, era ilegível na janela. */

const COLLAPSED = 6;

const kicker =
  "text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500";

// "16/10/26, 07:46" → "16/10 07:46"
const shortWhen = (value) =>
  String(value || "").replace(/^(\d{2}\/\d{2})\/\d{2},?\s*/, "$1 ");

const Footer = ({ children }) => (
  <div className="border-t border-slate-100 px-3 py-2">{children}</div>
);

const LinkButton = ({ to, onLink, children }) => (
  <button
    type="button"
    onClick={() => onLink(to)}
    className="text-[11.5px] font-bold text-[var(--primary)] hover:underline"
  >
    {children} ›
  </button>
);

const QueriesCard = ({ data, onLink }) => {
  const [expanded, setExpanded] = useState(false);
  const items = Array.isArray(data.items) ? data.items : [];
  const shown = expanded ? items : items.slice(0, COLLAPSED);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <span className={kicker}>{data.title}</span>
        <span className="text-[11px] tabular-nums text-slate-400">
          {data.total > items.length
            ? `${items.length} de ${data.total}`
            : items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-3 text-[12px] text-slate-500">
          Nenhuma consulta encontrada.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {shown.map((item, index) => (
            <li key={`${item.protocol || ""}-${index}`} className="px-3 py-2">
              <p className="truncate text-[12.5px] font-bold text-slate-900">
                {item.query}
              </p>
              <p className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px]">
                <span className="min-w-0 truncate tabular-nums text-slate-500">
                  {shortWhen(item.when)} · {item.document}
                  {item.user ? ` · ${item.user}` : ""}
                </span>
                <span
                  className={`shrink-0 font-semibold ${
                    item.success ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  {item.success ? "✓ com resultado" : "● falhou"}
                </span>
              </p>
            </li>
          ))}
        </ul>
      )}
      <Footer>
        <span className="flex items-center justify-between gap-2">
          {items.length > COLLAPSED ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800"
            >
              {expanded
                ? "Mostrar menos"
                : `Mostrar mais ${items.length - COLLAPSED}`}
            </button>
          ) : (
            <span />
          )}
          <LinkButton to={data.link || "/reports/queries"} onLink={onLink}>
            Ver em Relatórios
          </LinkButton>
        </span>
      </Footer>
    </div>
  );
};

const CatalogCard = ({ data, onLink }) => {
  const groups = Array.isArray(data.groups) ? data.groups : [];
  const additionals = Array.isArray(data.additionals) ? data.additionals : [];
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-3 py-2">
        <span className={kicker}>{data.title || "Consultas liberadas"}</span>
      </div>
      {groups.map((group) => (
        <div
          key={group.name}
          className="border-b border-slate-100 last:border-b-0"
        >
          <p className="flex items-center justify-between gap-2 bg-slate-50/80 px-3 py-1.5">
            <span className="text-[11.5px] font-bold text-slate-700">
              {group.name}{" "}
              <span className="font-semibold text-slate-400">
                · {group.items.length}
              </span>
            </span>
            {group.path && (
              <LinkButton to={group.path} onLink={onLink}>
                Abrir
              </LinkButton>
            )}
          </p>
          <ul className="divide-y divide-slate-100">
            {group.items.map((item) => (
              <li
                key={item.name}
                className="flex items-baseline justify-between gap-2 px-3 py-1.5 text-[12.5px]"
              >
                <span className="min-w-0 truncate text-slate-800">
                  {item.name}
                  {item.maintenance && (
                    <span className="ml-1.5 text-[10.5px] font-semibold text-amber-700">
                      ◆ em manutenção
                    </span>
                  )}
                </span>
                {item.price && (
                  <span className="shrink-0 font-bold tabular-nums text-slate-900">
                    {item.price}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {additionals.length > 0 && (
        <Footer>
          <p className={`${kicker} mb-1.5`}>
            Adicionais · na consulta de crédito
          </p>
          <div className="flex flex-wrap gap-1.5">
            {additionals.map((item) => (
              <span
                key={item.name}
                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11.5px] font-semibold text-slate-700"
              >
                ＋ {item.name}
                {item.price && (
                  <span className="ml-1 font-bold tabular-nums text-slate-500">
                    {item.price}
                  </span>
                )}
              </span>
            ))}
          </div>
        </Footer>
      )}
      {groups.length === 0 && additionals.length === 0 && (
        <p className="px-3 py-3 text-[12px] text-slate-500">
          Nenhuma consulta liberada.
        </p>
      )}
    </div>
  );
};

const CardShell = ({ title, aside, children, footer }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 px-3 py-2">
      <span className={kicker}>{title}</span>
      {aside != null && (
        <span className="text-[11px] tabular-nums text-slate-400">{aside}</span>
      )}
    </div>
    {children}
    {footer && <Footer>{footer}</Footer>}
  </div>
);

const Empty = ({ children }) => (
  <p className="px-3 py-3 text-[12px] text-slate-500">{children}</p>
);

const STATUS_TONE = {
  ativo: "text-emerald-700",
  bloqueado: "text-slate-500",
  inadimplente: "text-red-700",
};

// Carteira: um cliente por linha; com link, a linha abre a ficha.
const ClientsCard = ({ data, onLink }) => {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <CardShell
      title={data.title}
      aside={
        data.total > items.length
          ? `${items.length} de ${data.total}`
          : items.length
      }
      footer={
        <span className="flex justify-end">
          <LinkButton to={data.link || "/admin/customers"} onLink={onLink}>
            Ver clientes
          </LinkButton>
        </span>
      }
    >
      {items.length === 0 ? (
        <Empty>Nenhum cliente encontrado.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item, index) => {
            const body = (
              <>
                <p className="flex items-baseline justify-between gap-2">
                  <b className="min-w-0 truncate text-[12.5px] text-slate-900">
                    {item.name}
                  </b>
                  <span
                    className={`shrink-0 text-[11px] font-semibold ${STATUS_TONE[item.status] || "text-slate-500"}`}
                  >
                    {item.status}
                  </span>
                </p>
                <p className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-slate-500">
                  <span>
                    {item.lastQuery
                      ? `última consulta ${item.lastQuery}`
                      : "sem consultas"}
                  </span>
                  <span>· {item.monthQueries} no mês</span>
                  <span>· {item.payment}</span>
                  {item.balance && (
                    <span
                      className={
                        item.lowBalance ? "font-semibold text-amber-700" : ""
                      }
                    >
                      · saldo {item.balance}
                    </span>
                  )}
                  {item.overdue > 0 && (
                    <span className="font-semibold text-red-700">
                      · ● {item.overdue}{" "}
                      {item.overdue === 1
                        ? "boleto vencido"
                        : "boletos vencidos"}
                    </span>
                  )}
                </p>
              </>
            );
            return (
              <li key={`${item.name}-${index}`}>
                {item.link ? (
                  <button
                    type="button"
                    onClick={() => onLink(item.link)}
                    className="block w-full px-3 py-2 text-left transition hover:bg-slate-50"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="px-3 py-2">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
};

// Consumo por cliente: valor e margem; total do período no topo.
const PortfolioCard = ({ data, onLink }) => {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <CardShell
      title={data.title}
      footer={
        <span className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">
            {data.more ? `+ ${data.more} clientes` : ""}
          </span>
          <LinkButton to={data.link || "/admin/reports"} onLink={onLink}>
            Ver relatórios
          </LinkButton>
        </span>
      }
    >
      {data.totals && (
        <div className="grid grid-cols-3 gap-2 border-b border-slate-100 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-500">
          <span>
            Consultas
            <b className="block text-[13px] tabular-nums text-slate-900">
              {data.totals.count}
            </b>
          </span>
          <span>
            Valor
            <b className="block text-[13px] tabular-nums text-slate-900">
              {data.totals.total}
            </b>
          </span>
          <span>
            {data.marginLabel === "sua margem" ? "Sua margem" : "Margem"}
            <b className="block text-[13px] tabular-nums text-emerald-700">
              {data.totals.margin}
            </b>
          </span>
        </div>
      )}
      {items.length === 0 ? (
        <Empty>Nenhuma consulta no período.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item, index) => (
            <li key={`${item.name}-${index}`} className="px-3 py-2">
              <p className="flex items-baseline justify-between gap-2">
                <b className="min-w-0 truncate text-[12.5px] text-slate-900">
                  {item.name}
                </b>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-slate-900">
                  {item.total}
                </span>
              </p>
              <p className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px] text-slate-500">
                <span>
                  {item.count} {item.count === 1 ? "consulta" : "consultas"}
                </span>
                <span className="tabular-nums">
                  {data.marginLabel} {item.margin}
                  {item.marginPct != null
                    ? ` · ${String(item.marginPct).replace(".", ",")}%`
                    : ""}
                </span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
};

// Boletos em aberto por cliente, vencidos primeiro.
const InvoicesCard = ({ data, onLink }) => {
  const items = Array.isArray(data.items) ? data.items : [];
  const totals = data.totals || {};
  return (
    <CardShell
      title={data.title}
      aside={totals.total}
      footer={
        <span className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">
            {totals.overdueClients
              ? `● ${totals.overdueClients} ${totals.overdueClients === 1 ? "cliente com vencido" : "clientes com vencido"}`
              : ""}
          </span>
          <LinkButton to={data.link || "/admin/invoices"} onLink={onLink}>
            Ver financeiro
          </LinkButton>
        </span>
      }
    >
      {items.length === 0 ? (
        <Empty>Nenhum boleto em aberto.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item, index) => (
            <li key={`${item.name}-${index}`} className="px-3 py-2">
              <p className="flex items-baseline justify-between gap-2">
                <b className="min-w-0 truncate text-[12.5px] text-slate-900">
                  {item.name}
                </b>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-slate-900">
                  {item.total}
                </span>
              </p>
              <p className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px]">
                <span className="text-slate-500">
                  {item.open} em aberto
                  {item.oldest ? ` · desde ${item.oldest}` : ""}
                </span>
                {item.overdue > 0 && (
                  <span className="shrink-0 font-semibold text-red-700">
                    ● {item.overdue}{" "}
                    {item.overdue === 1 ? "vencido" : "vencidos"}
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
};

const AssistantCards = ({ attachments, onLink }) => {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) return null;
  return (
    <div className="space-y-2">
      {list.map((item) =>
        item.type === "queries" ? (
          <QueriesCard key="queries" data={item} onLink={onLink} />
        ) : item.type === "catalog" ? (
          <CatalogCard key="catalog" data={item} onLink={onLink} />
        ) : item.type === "clients" ? (
          <ClientsCard key="clients" data={item} onLink={onLink} />
        ) : item.type === "portfolio" ? (
          <PortfolioCard key="portfolio" data={item} onLink={onLink} />
        ) : item.type === "invoices" ? (
          <InvoicesCard key="invoices" data={item} onLink={onLink} />
        ) : null,
      )}
    </div>
  );
};

export default AssistantCards;
